// react-router-dom components
import { Link } from "react-router-dom";

// react-awesome-reveal
import { Fade } from "react-awesome-reveal";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";
import BackgroundBlogCard from "examples/Cards/BlogCards/BackgroundBlogCard";
import FilledInfoCard from "examples/Cards/InfoCards/FilledInfoCard";

// Presentation page sections
import InstagramFeed from "pages/Presentation/sections/Instagram";
import GoogleReviews from "pages/Presentation/sections/GoogleReviews";

// Constants
import CONSTANTS from "constants.js/constants";

// Data
import textData from "data/textData";
import specialties from "data/specialties";

// Routes
import navbarRoutes from "navbar.routes";
import footerRoutes from "footer.routes";

// Images
import bgImage from "assets/images/main/bg2.png";

function Presentation() {
  return (
    <>
      <DefaultNavbar
        routes={navbarRoutes}
        action={{
          type: "external",
          route: CONSTANTS.URL_WHATSAPP,
          label: textData.navbar.actionButton,
          color: "info",
        }}
        sticky
        sx={{
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { md } }) => md,
        }}
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "top",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Fade direction="down" triggerOnce>
            <Grid
              container
              item
              xs={12}
              lg={7}
              justifyContent="center"
              mx="auto"
              textAlign="center"
            >
              <MKTypography
                variant="h1"
                color="white"
                sx={({ breakpoints, typography: { size } }) => ({
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
              >
                {textData.presentationPage.hero.title}
              </MKTypography>
              <MKTypography variant="body1" color="white" mt={1} mb={3} px={{ xs: 0, lg: 6 }}>
                {textData.presentationPage.hero.subtitle}
              </MKTypography>
              <MKButton
                variant="gradient"
                color="info"
                size="large"
                component="a"
                href={CONSTANTS.URL_WHATSAPP}
                target="_blank"
              >
                {textData.presentationPage.hero.button}
              </MKButton>
            </Grid>
          </Fade>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={3}>
              {textData.presentationPage.infoCards.map(
                ({ id, color, title, description, variant }, index) => (
                  <Grid item xs={12} lg={4} key={id} sx={{ display: "flex" }}>
                    <Fade direction="up" delay={index * 100} triggerOnce>
                      <FilledInfoCard
                        variant={variant || "standard"}
                        color={color}
                        icon=""
                        title={title}
                        description={description}
                      />
                    </Fade>
                  </Grid>
                )
              )}
            </Grid>
          </Container>
        </MKBox>
        <MKBox
          component="section"
          py={8}
          sx={{ backgroundColor: ({ palette: { background } }) => background.default }}
        >
          <Container>
            <Fade direction="up" triggerOnce>
              <Grid
                container
                item
                xs={12}
                md={8}
                justifyContent="center"
                sx={{ mx: "auto", textAlign: "center", mb: 6 }}
              >
                <MKTypography variant="h3" mb={1.5}>
                  {textData.presentationPage.specialties.title}
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  {textData.presentationPage.specialties.subtitle}
                </MKTypography>
              </Grid>
            </Fade>
            <Grid container spacing={3}>
              {specialties.slice(0, 3).map((specialty, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={4}
                  key={specialty.id}
                  sx={{
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: ({ boxShadows: { md } }) => md,
                    },
                  }}
                >
                  <Fade direction="up" delay={index * 100} triggerOnce>
                    <BackgroundBlogCard
                      image={specialty.image}
                      title={specialty.title}
                      description={specialty.shortDescription}
                      action={{
                        type: "internal",
                        route: "/especialidades",
                      }}
                    />
                  </Fade>
                </Grid>
              ))}
            </Grid>
            <Fade direction="up" triggerOnce>
              <Grid container item xs={12} justifyContent="center" sx={{ mt: 6 }}>
                <MKButton
                  component={Link}
                  to="/especialidades"
                  variant="gradient"
                  color="info"
                  size="large"
                >
                  {textData.presentationPage.specialties.button}
                </MKButton>
              </Grid>
            </Fade>
          </Container>
        </MKBox>
        <MKBox
          component="section"
          py={8}
          sx={{ backgroundColor: ({ palette: { background } }) => background.default }}
        >
          <Fade direction="up" triggerOnce>
            <Container>
              <Grid container justifyContent="center" sx={{ textAlign: "center" }}>
                <Grid item xs={12} md={8}>
                  <MKTypography variant="h3" mb={1.5}>
                    {textData.presentationPage.reviews.title}
                  </MKTypography>
                  <MKTypography variant="body1" color="text">
                    {textData.presentationPage.reviews.subtitle}
                  </MKTypography>
                </Grid>
              </Grid>
              <GoogleReviews />
            </Container>
          </Fade>
        </MKBox>
        <MKBox
          component="section"
          py={8}
          sx={{ backgroundColor: ({ palette: { white } }) => white.main }}
        >
          <Fade direction="up" triggerOnce>
            <Container>
              <Grid container justifyContent="center" sx={{ textAlign: "center" }}>
                <Grid item xs={12} md={8}>
                  <MKTypography variant="h3" mb={1}>
                    {textData.presentationPage.instagram.title}
                  </MKTypography>
                  <MKTypography variant="body1" color="text" mb={2}>
                    {textData.presentationPage.instagram.subtitle}
                  </MKTypography>
                </Grid>
              </Grid>
              <InstagramFeed />
            </Container>
          </Fade>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Presentation;
